import { useLayoutEffect, useRef } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { useCompany } from '@/company/context';
import type { ConnectionReadItem } from '../connection-read-access/api';

/** Component identity, including the selected grant, so stale drafts are never repinned. */
export function useConnectionContextKey(item?: ConnectionReadItem) {
  const { data } = useCompany();
  return JSON.stringify([
    data?.company?.id,
    data?.membership?.userId,
    data?.membership?.role,
    data?.membership?.permissions?.manageConnections,
    item?.connectionId,
    item?.bindingVersion,
    item?.access.revision,
    item?.canSet,
  ]);
}

/** One action for this mounted context; old completion cannot clear a newer action. */
export function useCurrentConnectionRequest() {
  const fetch = useSessionBoundFetch();
  const lifetime = useRef<object | null>(null);
  const active = useRef<object | null>(null);
  const currentFetch = useRef(fetch);
  currentFetch.current = fetch;
  useLayoutEffect(() => {
    const owner = {};
    active.current = null;
    lifetime.current = owner;
    return () => {
      if (lifetime.current === owner) lifetime.current = null;
    };
  }, [fetch]);
  return {
    fetch,
    isMounted: () => lifetime.current !== null && currentFetch.current === fetch,
    begin: () => {
      if (!lifetime.current || active.current) return null;
      const owner = lifetime.current;
      const operation = {};
      active.current = operation;
      const current = () =>
        lifetime.current === owner &&
        currentFetch.current === fetch &&
        active.current === operation;
      return {
        fetch,
        current,
        finish: () => {
          const applies = current();
          if (active.current === operation) active.current = null;
          return applies;
        },
      };
    },
  };
}
